<template>
	<v-container grid-list-md>
		<ValidationObserver
			ref="obs"
			v-slot="{ invalid }"
			:key="reservationKey"
		>
			<v-card max-width="658px" class="mx-auto">
				<v-list-item class="my-0 py-0">
					<v-list-item-action v-if="item.tenant">
						<v-img
							:src="item.tenant.image_url"
							width="80"
							height="80"
							contain
						></v-img>
					</v-list-item-action>
					<v-list-item-content v-if="item.outlet">
						<v-list-item-title
							style="font-size: 24px; font-weight: bold"
						>
							{{ item.outlet.name }}
						</v-list-item-title>
						<v-list-item-subtitle>
							{{ item.outlet.opening_time }}
						</v-list-item-subtitle>
					</v-list-item-content>
					<v-list-item-action>
						<v-select
							style="width: 100px"
							dense
							single-line
							hide-details
							v-model="locale"
							:items="locales"
							@change="changeLocale"
							item-value="value"
							item-text="name"
						>
						</v-select>
					</v-list-item-action>
				</v-list-item>
				<v-divider></v-divider>
				<v-card-text>
					<v-layout row wrap>
						<v-flex xs12>
							<v-alert type="info" v-if="!item.is_editable" dense
								><span
									v-html="
										$__(
											'message.this_reservation_is_not_editable'
										)
									"
								></span
							></v-alert>
							<v-alert
								type="success"
								v-if="success"
								color="secondary"
								dismissible
								dense
								><span v-html="success"></span
							></v-alert>
							<v-alert type="error" v-if="error" dismissible dense
								><span v-html="error"></span
							></v-alert>
						</v-flex>
						<v-flex xs12 md6>
							{{ $__("Code") }}: {{ item.code }} <br />
							{{ $__("Status") }}:
							<span :style="{ color: item.status_color }">{{
								item.status
							}}</span>
							<br />
							{{ $__("Created at") }}: {{ item.created_at }}
						</v-flex>
						<v-flex xs12 md6>
							{{ $__("Name") }}: {{ item.name }} <br />
							{{ $__("Phone") }}: {{ item.phone }} <br />
							{{ $__("Email") }}: {{ item.email }} <br />
						</v-flex>
						<v-flex xs12>&nbsp;</v-flex>
						<v-flex xs12 md6>
							<ValidationProvider
								name="adults"
								rules="required"
								v-slot="{ errors }"
							>
								<v-select
									:items="adults"
									v-model="item.adults"
									:label="$__('Adults')"
									prepend-inner-icon="mdi-account-multiple"
									filled
									dense
									:error-messages="errors"
									class="required"
								>
								</v-select>
							</ValidationProvider>
						</v-flex>
						<v-flex xs12 md6>
							<ValidationProvider
								name="children"
								rules="required"
								v-slot="{ errors }"
							>
								<v-select
									:items="children"
									v-model="item.children"
									prepend-inner-icon="mdi-baby"
									:label="$__('Children')"
									filled
									dense
									:error-messages="errors"
									class="required"
								>
								</v-select>
							</ValidationProvider>
						</v-flex>
						<v-flex xs12 md6>
							<v-menu
								v-model="date"
								:close-on-content-click="false"
								max-width="290"
							>
								<template v-slot:activator="{ on, attrs }">
									<ValidationProvider
										name="date"
										rules="required"
										v-slot="{ errors }"
									>
										<v-text-field
											:value="formattedDate"
											:label="$__('Date')"
											readonly
											v-bind="attrs"
											v-on="on"
											@click:clear="item.date = null"
											prepend-inner-icon="mdi-calendar"
											data-vv-name="date"
											class="required"
											filled
											dense
											:error-messages="errors"
										></v-text-field>
									</ValidationProvider>
								</template>
								<v-date-picker
									v-model="item.date"
									@change="date = false"
									:min="today"
								></v-date-picker>
							</v-menu>
						</v-flex>
						<v-flex xs12 md6>
							<ValidationProvider
								name="time"
								rules="required"
								v-slot="{ errors }"
							>
								<v-combobox
									:label="$__('Time')"
									:items="times"
									v-model="item.time"
									item-value="value"
									item-text="text"
									:return-object="false"
									prepend-inner-icon="mdi-clock"
									data-vv-name="reservation.time"
									class="required"
									:loading="loading"
									loader-height="5"
									filled
									dense
									:error-messages="errors"
								>
								</v-combobox>
							</ValidationProvider>
						</v-flex>
						<v-flex xs12 md12>
							<v-textarea
								:label="$__('Comment')"
								prepend-icon="mdi-comment"
								v-model="item.description"
								rows="3"
								data-vv-name="description"
							>
							</v-textarea>
						</v-flex>
					</v-layout>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-btn
						text
						@click="cancelDialog = true"
						v-if="item.is_cancellable"
					>
						{{ $__("Cancel") }}
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						color="primary"
						@click="submit()"
						:disabled="invalid"
						:loading="loading"
						v-if="item.is_editable"
					>
						{{ $__("Update Reservation") }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</ValidationObserver>

		<v-dialog
			v-model="cancelDialog"
			persistent
			max-width="350"
			v-if="item.is_cancellable"
		>
			<v-card>
				<v-card-title class="text-h5">
					Cancel Reservation?
				</v-card-title>
				<v-card-text class="pa-0">
					<v-container grid-list-xs>
						<v-layout wrap row>
							<v-flex xs12>
								Are you sure want to cancel the reservation? you
								can edit the information instead.
							</v-flex>
							<v-flex xs12>
								<v-textarea
									v-model="cancelReason"
									:label="$__('Cancel Reservation')"
								></v-textarea>
							</v-flex>
						</v-layout>
					</v-container>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn
						color="red darken-1"
						text
						@click="cancel()"
						:loading="canceling"
					>
						{{ $__("Cancel") }}
					</v-btn>
					<v-btn @click="cancelDialog = false" color="primary">
						{{ $__("Close") }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import { format, parseISO } from "date-fns";
import axios from "axios";
export default {
	props: ["reservation", "adults", "children"],

	data() {
		return {
			item: this.reservation,
			reservationKey: 0,
			times: [],
			loading: false,
			canceling: false,
			today: format(parseISO(new Date().toISOString()), "yyyy-MM-dd"),
			date: false,
			locale: this.$locale(),
			success: null,
			error: null,
			cancelDialog: false,
			cancelReason: "",
		};
	},

	computed: {
		formattedDate() {
			return this.item.date
				? format(parseISO(this.item.date), "EEEE, MMMM do yyyy")
				: "";
		},

		formattedTime() {
			return this.item.time && this.item.date
				? format(
						parseISO(this.item.date + " " + this.item.time),
						"h:mm a"
				  )
				: "";
		},

		locales() {
			return [
				{
					value: "en",
					name: "English",
				},
				{
					value: "km",
					name: "ភាសាខ្មែរ",
				},
				{
					value: "fr",
					name: "Français",
				},
				{
					value: "zh",
					name: "中文",
				},
			];
		},
	},

	watch: {
		"item.date": {
			handler() {
				this.getTimes();
			},
			immediate: true,
		},
	},

	methods: {
		getTimes() {
			try {
				if (this.item.date && this.item.outlet.uuid) {
					this.loading = true;
					axios
						.get(
							`/widget/reservation/${this.item.outlet.uuid}/times`,
							{
								params: {
									date: this.item.date,
								},
							}
						)
						.then((response) => {
							if (response.data.success) {
								this.times = response.data.data;
							} else {
								this.success = null;
								this.error = response.data.data;
							}
						})
						.catch((error) => {
							this.success = null;
							this.error = error;
						})
						.finally(() => {
							this.loading = false;
						});
				}
			} catch (error) {
				this.success = null;
				this.error = error;
			}
		},

		submit() {
			try {
				this.loading = true;
				axios
					.put(
						`/widget/reservation/${this.item.uuid}/update`,
						this.item
					)
					.then((response) => {
						if (response.data.success) {
							this.success = response.data.data;
							this.error = null;
						} else {
							this.success = null;
							this.error = response.data.data;
						}
					})
					.catch((error) => {
						this.success = null;
						this.error = error;
					})
					.finally(() => {
						this.loading = false;
					});
			} catch (error) {
				this.success = null;
				this.error = error;
			}
		},

		changeLocale() {
			this.$setLocale(this.locale);
			this.$forceUpdate();
			this.reservationKey += 1;
		},

		cancel() {
			try {
				this.canceling = true;
				axios
					.delete(`/widget/reservation/${this.item.uuid}/cancel`, {
						params: {
							reason: this.cancelReason,
						},
					})
					.then((response) => {
						if (response.data.success) {
							this.item = response.data.data.reservation;
							this.success = response.data.data.message;
							this.error = null;
						} else {
							this.success = null;
							this.error = response.data.data;
						}
					})
					.catch((error) => {
						this.success = null;
						this.error = error;
					})
					.finally(() => {
						this.canceling = false;
						this.cancelDialog = false;
						this.cancelReason = "";
					});
			} catch (error) {
				this.success = null;
				this.error = error;
			}
		},
	},
};
</script>